import React, { useState, FormEvent, useEffect } from 'react';
import { Suggest, ItemRenderer, ItemPredicate } from '@blueprintjs/select';
import { FormGroup, InputGroup, AnchorButton, Button, Checkbox, HTMLTable, MenuItem, Spinner } from "@blueprintjs/core";
import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import { Voucher, VoucherItem, Environment, NumData } from './Types'
import * as utils from "./utils";

interface JournalListingProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function JournalListing({ svc, currentEnv = {} as Environment }: JournalListingProps) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState([] as Voucher[])
    const [numList, setNumList] = useState([] as NumData[])

    let date = new Date(), y = date.getFullYear(), m = date.getMonth()
    const [fromJrnCode, setFromJrnCode] = useState("")
    const [toJrnCode, setToJrnCode] = useState("")
    const [fromDate, setFromDate] = useState("")
    const [toDate, setToDate] = useState("")
    const [from, setFrom] = useState(utils.formatDate(new Date(y, m, 1)))
    const [to, setTo] = useState(utils.formatDate(date)) // new Date(y, m+1, 0)

    const refreshNumList = async () => {
        const res = await svc.post('/numlist',
            {
                type: "J",
            });
            setNumList([{type: "J", group:"Any", last:0} as NumData].concat(res.data.data))
            //setNumList(res.data.data)
    }

    useEffect(() => {
        if ("envId" in currentEnv) {
            refreshNumList()
        }
    }, [svc, currentEnv])
    
    const numRenderer: ItemRenderer<NumData> = (num, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }
        const text = `${num.group}`;
        return (
            <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                label={num.group}
                key={num.group}
                onClick={handleClick}
                text={highlightText(text, query)}
            />
        );
    };

    const numPredicate: ItemPredicate<NumData> = (query, num, _index, exactMatch) => {
        const normalizedText = `${num.group}`.toLowerCase();
        const normalizedQuery = query.toLowerCase();

        if (exactMatch) {
            return normalizedText === normalizedQuery;
        } else {
            return normalizedText.indexOf(normalizedQuery) >= 0;
        }
    };

    const numValueRenderer = (num: NumData) => {
        return num.group
    }

    const createNumDataFromQuery = (query: string) => {
        return {group: query.toUpperCase(), type: "J", last: 0} as NumData
    }

    const createNumDataRenderer = (
        query: string,
        active: boolean,
        handleClick: React.MouseEventHandler<HTMLElement>,
    ) => (
        <MenuItem
            icon="manually-entered-data"
            text={`${query}`}
            active={active}
            onClick={handleClick}
            shouldDismissPopover={false}
        />
    );

    function highlightText(text: string, query: string) {
        let lastIndex = 0;
        const words = query
            .split(/\s+/)
            .filter(word => word.length > 0)
            .map(escapeRegExpChars);
        if (words.length === 0) {
            return [text];
        }
        const regexp = new RegExp(words.join("|"), "gi");
        const tokens: React.ReactNode[] = [];
        while (true) {
            const match = regexp.exec(text);
            if (!match) {
                break;
            }
            const length = match[0].length;
            const before = text.slice(lastIndex, regexp.lastIndex - length);
            if (before.length > 0) {
                tokens.push(before);
            }
            lastIndex = regexp.lastIndex;
            tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
        }
        const rest = text.slice(lastIndex);
        if (rest.length > 0) {
            tokens.push(rest);
        }
        return tokens;
    }

    function escapeRegExpChars(text: string) {
        //return text.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        return text.replace(/([.*+?^=!:${}()|\\])/g, "\\$1");
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/voucherlist',
                {
                    by: "journalCode",
                    start: fromJrnCode,
                    voucherCode: "",
                    limit: 0,
                    backward: false,
                    filters: {
                        fromJournal: fromJrnCode,
                        toJournal: toJrnCode,
                        from: from,
                        to: to,
                        reference: "",
                        desc: "",
                    }
                });

            setIsLoading(false)
            setFromDate(from)
            setToDate(to)
            setData(res.data.data)
        } catch (error) {
            utils.showError(error)
        }
    }

    let pageMaxWidth = currentEnv.multiCurrency ? "1850px" : "1400px"
    let pageTitle = t('journal_listing')
    let list = <div className="Print-Align-C Print-90-Percent" style={{maxWidth: pageMaxWidth}}>
        <div className="Spacing-V-16" />
        <div className="No-Screen">
            <div className="Row" style={{maxWidth: pageMaxWidth}}>
                <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{currentEnv.companyName}</div>
            </div>
            <div className="Spacing-V-8" />
            {currentEnv.companyAddress && currentEnv.companyAddress.split('\n').map((line) =>
                <div className="Row" style={{maxWidth: pageMaxWidth}}>
                    <div className="Col Align-C">{line}</div>
                </div>
            )}
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{pageTitle}</div>
        </div>
        {isLoading && <Spinner></Spinner>}
        <div className="Spacing-V-8" />
        {/*
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "14px", fontWeight: 600}}>Account From: {data.fromAcCode === "" ? "Any" : data.fromAcCode}<div className="Spacing-H-8"/>To: {data.toAcCode === "" ? "Any" : data.toAcCode}</div>
        </div>
        */}
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('from')}: {fromDate}<div className="Spacing-H-8"/>{t('to')}: {toDate}</div>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-Journal">{t('journal')}</th>
                    <th className="TCol-Voucher">{t('voucher')}</th>
                    <th className="TCol-Reference">{t('reference')}</th>
                    <th className="TCol-Date">{t('date')}</th>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-Desc">{t('description')}</th>
                    {currentEnv.multiCurrency && <>
                    <th className="TCol-Currency">{t('currency')}</th>
                    <th className="TCol-ExchangeRate">{t('exchange_rate')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('fdr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('fcr')}</div></th>
                    <th className="THead-Sep"></th>
                    </>}
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                </tr>
            </thead>
            <tbody>
                {data && data.map((vch: Voucher) => <>
                    {vch.items?.map((journal: VoucherItem, i: Number) =>
                    <tr>
                        <td className="TCol-Journal">{i === 0 && vch.journalCode}</td>
                        {/*<td className="TCol-Voucher">{i === 0 && <a target="_blank" href={`#/vouchers/detail/${vch.journalCode}/${vch.voucherCode}`}>{vch.voucherCode}</a>}</td>*/}
                        <td className="TCol-Voucher"><a target="_blank" href={`#/${utils.journalBase(vch.type)}/detail/${vch.journalCode}/${vch.voucherCode}`}>{vch.voucherCode}</a></td>
                        <td className="TCol-Reference">{i === 0 && vch.reference}</td>
                        <td className="TCol-Date">{i === 0 && vch.txDate}</td>
                        <td className="TCol-ACCode">{journal.acCode}</td>
                        <td className="TCol-ACName">{journal.acName}</td>
                        <td className="TCol-Desc">{journal.description1}</td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Currency">{journal.ccyCode === "" ? currentEnv.baseCurrency : journal.ccyCode}</td>
                        <td className="TCol-ExchangeRate">{journal.exRate}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{journal.dr > 0 && utils.formatMoney(journal.fdr)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{journal.cr > 0 && utils.formatMoney(journal.fcr)}</div></td>
                        <td className="TCol-Sep"></td>
                        </>}
                        <td className="TCol-ACBalance"><div className="Align-R">{journal.dr > 0 && utils.formatMoney(journal.dr)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{journal.cr > 0 && utils.formatMoney(journal.cr)}</div></td>
                    </tr>
                    )}
                    {/* <tr>
                        <td className="TCol-Journal"></td>
                        <td className="TCol-Voucher"></td>
                        <td className="TCol-Reference"></td>
                        <td className="TCol-Date"></td>
                        <td className="TCol-ACCode"></td>
                        <td className="TCol-ACName"></td>
                        <td className="TCol-Desc"><b>{t('tran_total')}</b></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(vch.dr)}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(vch.cr)}</b></div></td>
                    </tr> */}
                    {Object.entries(vch.fbal).map((bal, i) => <>
                    <tr>
                        <td className="TCol-Journal"></td>
                        <td className="TCol-Voucher"></td>
                        <td className="TCol-Reference"></td>
                        <td className="TCol-Date"></td>
                        <td className="TCol-ACCode"></td>
                        <td className="TCol-ACName"></td>
                        <td className="TCol-Desc">{i === 0 && <b>{t('tran_total')}</b>}</td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Currency">{bal[0] === "" ? currentEnv.baseCurrency : bal[0]}</td>
                        <td className="TCol-ExchangeRate"></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(bal[1].fdr)}</b></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.formatMoney(bal[1].fcr)}</b></div></td>
                        <td className="TCol-Sep"></td>
                        </>}
                        <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(vch.dr)}</b></div>}</td>
                        <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.formatMoney(vch.cr)}</b></div>}</td>
                    </tr>
                    </>)}
                    </>
                )}
            </tbody>
        </HTMLTable>
    </div>

    return (
        <>
            <form className="No-Print" onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('from')} labelFor="fromJrnCode">
                        <Suggest
                            inputProps={{ placeholder: t('journal'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                            items={numList}
                            itemRenderer={numRenderer}
                            itemPredicate={numPredicate}
                            inputValueRenderer={numValueRenderer}
                            createNewItemFromQuery={createNumDataFromQuery}
                            createNewItemRenderer={createNumDataRenderer}
                            resetOnClose={true}
                            onItemSelect={(num) => {
                                setFromJrnCode(num.group === "Any" ? "" : num.group)
                            }}
                        />
                    </FormGroup>
                    <FormGroup className="Col-2-1" label={t('to')} labelFor="toJrnCode">
                        <Suggest
                            inputProps={{ placeholder: t('journal'), onKeyDown: (e) => {e.key === "Enter" && e.preventDefault()} }}
                            items={numList}
                            itemRenderer={numRenderer}
                            itemPredicate={numPredicate}
                            inputValueRenderer={numValueRenderer}
                            createNewItemFromQuery={createNumDataFromQuery}
                            createNewItemRenderer={createNumDataRenderer}
                            resetOnClose={true}
                            onItemSelect={(num) => {
                                setToJrnCode(num.group === "Any" ? "" : num.group)
                            }}
                        />
                    </FormGroup>
                    <FormGroup className="Col-3-1" label={t('from')} labelFor="from">
                        <InputGroup id="from" value={from} type="date" onChange={utils.handleStringChange(s => setFrom(s))} />
                    </FormGroup>
                    <FormGroup className="Col-4-1" label={t('to')} labelFor="to">
                        <InputGroup id="to" value={to} type="date" onChange={utils.handleStringChange(s => setTo(s))} />
                    </FormGroup>
                    <Button className="Col-5-1 Button-LabeledFormGroup" icon="desktop" type="submit" >{t('preview')}</Button>
                    <AnchorButton className="Col-6-1 Button-LabeledFormGroup" icon="download" target="_blank" href={`${svc.defaults.baseURL}/downloadjl?fromJournal=${fromJrnCode}&toJournal=${toJrnCode}&from=${from}&to=${to}`}>Excel</AnchorButton>
                    {/*<AnchorButton className="Col-4-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadtb?from=${from}&to=${to}&inline`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>PDF</AnchorButton>*/}
                    {/*<AnchorButton className="Col-5-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadtb?from=${from}&to=${to}`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>Download PDF</AnchorButton>*/}
                </div>
                {/*
                <div className="Row">
                    <Checkbox className="Col-1-1" label={t('includes_unposted')} checked={unposted} onChange={utils.handleBooleanChange(v => setUnposted(v))} />
                </div>
                */}
            </form>
            {list}
        </>
    );
}

export default JournalListing